<template>
  <div ref="contentVideoRef" class="content-video _ui-grid">
    <div class="video-container">
      <VideoJSPlayer
        v-if="data.asset?.mimeType.includes('video')"
        class="video"
        :class="{ 'video-ready': videoReady }"
        :poster="posterImage"
        :options="{
          autoplay: data.autoplay,
          muted: data.muted,
          controls: false,
          loop: data.loop,
          auto: true,
          fill: true,
          responsive: true,
          playsinline: true,
          hls: {
            limitRenditionByPlayerDimensions: true,
            useDevicePixelRatio: true,
          },
          bandwidth: 10e6,
          sources: [
            {
              src: videoSource,
              type: 'application/x-mpegURL',
            },
          ],
        }"
        @player-ready="onVideoReady"
        @play="onPlay"
        @pause="onPause"
        @ended="onEnded"
        @timeupdate="onTimeUpdate"
      />
      <div
        class="controls"
        :class="{ 'is-playing': isPlaying, 'is-paused': isPaused }"
      >
        <button class="play-button" @click="onPressPlay">
          <img :src="'/icons/play-button.png'" />
        </button>
        <div class="play-pause-button-container" @click="onPressPlayPause">
          <button class="play-pause-button">
            <img class="play-icon" :src="'/icons/Play.svg'" />
            <img class="pause-icon" :src="'/icons/Pause.svg'" />
          </button>
        </div>
        <button
          class="mute-button"
          :class="{ 'is-muted': isMuted }"
          @click="onPressMute"
        >
          <img class="mute-icon" :src="'/icons/Mute.svg'" />
          <img class="muted-icon" :src="'/icons/Muted.svg'" />
        </button>
        <div class="progress" @click="onPressProgress">
          <div class="bar"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import type { ContentVideoFragment } from '#gql';
import VideoJSPlayer from '~/components/partials/VideoJSPlayer.vue';
import { useHeaderIntersection } from '~/composables/useHeaderIntersection';

const props = defineProps<{
  data: ContentVideoFragment;
}>();

const videoReady = ref(false);
const isPlaying = ref(props.data.autoplay);
const isPaused = ref(false);
const isMuted = ref(props.data.muted);
let videoPlayer: any;

// Check if the content is intersecting with the header
const contentVideoRef = ref<HTMLElement | null>(null);
// The computed is needed because expose with option API is not working
const triggerElement = computed<HTMLElement | null>(
  () => contentVideoRef.value?.querySelector('.video') || null,
);

useHeaderIntersection(triggerElement);

const videoSource = computed(() => {
  if (!props.data.asset?.mimeType.includes('video')) return '';
  const asset = props.data.asset as {
    video?: { streamingUrl: string };
  };
  return asset.video?.streamingUrl ?? '';
});

function onVideoReady(player: any) {
  videoReady.value = true;
  videoPlayer = player;
}

function onPressPlay() {
  isPlaying.value = true;
  videoPlayer.play();
}

function onPressPlayPause() {
  if (isPlaying.value) {
    isPaused.value = true;
    videoPlayer.pause();
  } else {
    isPaused.value = false;
    videoPlayer.play();
  }
}

function onPressMute() {
  isMuted.value = !isMuted.value;
  videoPlayer.muted(isMuted.value);
}

function onPressProgress(event: MouseEvent) {
  const progressBar = event.currentTarget as HTMLElement;
  const progressBarWidth = progressBar.offsetWidth;
  const clickPosition =
    event.clientX - progressBar.getBoundingClientRect().left;
  const newTime = (clickPosition / progressBarWidth) * videoPlayer.duration();
  videoPlayer.currentTime(newTime);
}

function onPlay() {
  isPlaying.value = true;
}

function onPause() {
  isPlaying.value = false;
  videoPlayer.pause();
}

function onEnded() {
  isPlaying.value = false;
}

function onTimeUpdate(timePercentage: number) {
  gsap.to('.progress .bar', {
    width: `${timePercentage * 100}%`,
  });
}

const posterImage = computed(() => {
  console.log(props.data.thumbnail);
  if (props.data.thumbnail?.responsiveImage?.src) {
    return props.data.thumbnail?.responsiveImage?.src;
  }
  return undefined;
});
</script>

<style scoped lang="scss">
.content-video {
  width: 100%;
  min-height: 100%;
  height: auto;
  position: relative;
  @media (--vl) {
    padding-inline: unset !important;
  }

  .video-container {
    position: relative;
    grid-column: 3/-3;
    border-radius: var(--base-border-radius-small);
    overflow: hidden;
    background-color: var(--base-color-background);
    .video {
      aspect-ratio: 16/9;
      height: 100%;
      width: 100%;

      display: none;
      &.video-ready {
        display: block;
      }
    }
    .controls {
      position: absolute;
      inset: 0;
      display: grid;

      &.is-playing {
        .play-button {
          opacity: 0;
          pointer-events: none;
        }
        .play-pause-button-container {
          pointer-events: auto;
        }
        .play-pause-button,
        .mute-button,
        .progress {
          opacity: 1;
          pointer-events: auto;
        }
      }
      &.is-paused {
        .play-button {
          opacity: 0;
          pointer-events: none;
        }
        .play-pause-button-container {
          pointer-events: auto;
        }
        .play-pause-button {
          .play-icon {
            opacity: 1;
          }
          .pause-icon {
            opacity: 0;
          }
        }
        .play-pause-button,
        .mute-button,
        .progress {
          opacity: 1;
          pointer-events: auto;
        }
      }

      button {
        background-color: transparent;
        border: none;
        cursor: pointer;
      }
      .play-pause-button,
      .mute-button,
      .progress {
        opacity: 0;
        pointer-events: none;
      }
      .play-button {
        margin: auto;
        height: 5.75rem;
        width: 5.75rem;
        @media (--vs) {
          height: 2.875rem;
          width: 2.875rem;
          padding: 0;
        }
      }
      .play-pause-button-container {
        position: absolute;
        pointer-events: none;
        inset: 0;
      }
      .play-pause-button {
        position: absolute;
        top: calc(100% - 4.5rem);
        height: 1.5rem;
        width: 1.5rem;
        left: 1.5rem;
        .play-icon,
        .pause-icon {
          transition: opacity 0.3s ease-in-out;
          position: absolute;
          inset: 0;
          margin: auto;
        }
        .play-icon {
          opacity: 0;
        }
        .pause-icon {
          opacity: 1;
        }
      }
      .mute-button {
        position: absolute;
        top: calc(100% - 4.5rem);
        height: 1.5rem;
        width: 1.5rem;
        right: 1.5rem;
        .mute-icon,
        .muted-icon {
          transition: opacity 0.3s ease-in-out;
          position: absolute;
          inset: 0;
          margin: auto;
        }
        .mute-icon {
          opacity: 1;
        }
        .muted-icon {
          opacity: 0;
        }
        &.is-muted {
          .mute-icon {
            opacity: 0;
          }
          .muted-icon {
            opacity: 1;
          }
        }
      }
      .progress {
        position: absolute;
        top: calc(100% - 1.875rem);
        width: calc(100% - 3rem);
        height: 4px;
        background: var(--base-color-white-40);
        display: flex;
        border-radius: 2px;
        overflow: hidden;
        left: 1.5rem;
        right: 1.5rem;
        cursor: pointer;
        .bar {
          width: 0%;
          height: 4px;
          background: var(--color-white);
        }
      }
    }
  }
}
</style>
